import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext, Component } from "react";
import axios from "axios";

// ── Global 401 interceptor ────────────────────────────────────────────
// When a JWT expires (24h lifetime) or is invalidated, the backend
// returns 401 "Token expired" / "Invalid token". This interceptor
// catches that uniformly, clears local auth state, and bounces the
// user to /login with a clear toast instead of surfacing a confusing
// "Token expired" message inside whichever feature happened to call
// the API first. Auth endpoints themselves are exempt (we don't want
// to redirect away from /login when a login attempt fails with 401).
const _AUTH_PATH_EXEMPTIONS = ["/api/auth/login", "/api/auth/register", "/api/auth/refresh"];
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      const detail = error?.response?.data?.detail || "";
      const isAuthExempt = _AUTH_PATH_EXEMPTIONS.some((p) => url.includes(p));
      const isAuthError = status === 401 && !isAuthExempt;
      if (isAuthError) {
        // Clear cached auth so a stale token can't trigger a retry loop.
        try {
          localStorage.removeItem("token");
          localStorage.removeItem("heyintake_token");
        } catch (_) { /* localStorage unavailable in some sandboxed contexts */ }
        // Deferred toast (sonner may not be mounted yet on first paint).
        import("sonner").then(({ toast }) => {
          toast.error(detail || "Session expired. Please sign in again.");
        }).catch(() => {});
        // Avoid double-bouncing if we're already on /login or other public pages.
        const path = window.location?.pathname || "";
        const publicPaths = ["/login", "/register", "/forgot-password", "/reset-password"];
        const onPublic = publicPaths.some((p) => path.startsWith(p));
        if (!onPublic) {
          // Preserve where the user was so we can bounce back after re-login.
          const next = encodeURIComponent(path + (window.location?.search || ""));
          window.location.href = `/login?next=${next}`;
        }
      }
    } catch (_) {
      /* Never swallow the original error if the interceptor itself fails. */
    }
    return Promise.reject(error);
  },
);


// Universal Referral Tracking - captures ref/utm params on ANY page
import { captureReferralParams } from "@/utils/referralTracking";

// Error Boundary for catching React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-lg">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-700 mb-4">{this.state.error?.message}</p>
            <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-48">
              {this.state.errorInfo?.componentStack}
            </pre>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Contexts
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import PipelinePage from "@/pages/PipelinePage";
import SubmissionsPage from "@/pages/SubmissionsPage";
import ContactsPage from "@/pages/ContactsPage";
import SupportPage from "@/pages/SupportPage";
import FormBuilderPage from "@/pages/FormBuilderPage";
import SettingsPage from "@/pages/SettingsPage";
import IntakeFormPage from "@/pages/IntakeFormPage";
import CustomersPage from "@/pages/CustomersPage";
import AboutPage from "@/pages/AboutPage";
import DemoPage from "@/pages/DemoPage";
import FaqPage from "@/pages/FaqPage";
import PartnerPage from "@/pages/PartnerPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import LeadIntelligencePage from "@/pages/LeadIntelligencePage";
import AdminLeadIntelligencePage from "@/pages/AdminLeadIntelligencePage";
import TeamPage from "@/pages/TeamPage";
import JoinTeamPage from "@/pages/JoinTeamPage";
import DocsPage from "@/pages/DocsPage";
import DocsArticlePage from "@/pages/DocsArticlePage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import CompleteSignupPage from "@/pages/CompleteSignupPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ScrollToTop from "@/components/ScrollToTop";
import BillingGuard from "@/components/BillingGuard";
import EnvironmentBadge from "@/components/EnvironmentBadge";

// Industry Pages
import LawyersPage from "@/pages/industries/LawyersPage";
import ConsultantsPage from "@/pages/industries/ConsultantsPage";
import CoachesPage from "@/pages/industries/CoachesPage";
import AgenciesPage from "@/pages/industries/AgenciesPage";
import ContractorsPage from "@/pages/industries/ContractorsPage";
import AccountantsPage from "@/pages/industries/AccountantsPage";
import TherapistsPage from "@/pages/industries/TherapistsPage";
import PhotographersPage from "@/pages/industries/PhotographersPage";
import RealEstateAgentsPage from "@/pages/industries/RealEstateAgentsPage";
import FitnessTrainersPage from "@/pages/industries/FitnessTrainersPage";
import MedSpasPage from "@/pages/industries/MedSpasPage";
import CleaningServicesPage from "@/pages/industries/CleaningServicesPage";
import EventPlannersPage from "@/pages/industries/EventPlannersPage";
import DentistsPage from "@/pages/industries/DentistsPage";
import InteriorDesignersPage from "@/pages/industries/InteriorDesignersPage";
import FinancialAdvisorsPage from "@/pages/industries/FinancialAdvisorsPage";

// Comparison Pages
import HeyIntakeVsIntakeQVsJotform from "@/pages/compare/HeyIntakeVsIntakeQVsJotform";

// Blog Pages
import BlogIndexPage from "@/pages/blog/BlogIndexPage";
import LawFirmAIIntakeBlogPost from "@/pages/blog/LawFirmAIIntakeBlogPost";
import LeadQualificationToolsBlogPost from "@/pages/blog/LeadQualificationToolsBlogPost";
import AutomateClientScreeningBlogPost from "@/pages/blog/AutomateClientScreeningBlogPost";

// Workflow Pages (SLICE 3)
import EmailTemplatesPage from "@/pages/EmailTemplatesPage";
import AutomationsPage from "@/pages/AutomationsPage";

// Client Portal (PUBLIC)
import ClientPortalPage from "@/pages/ClientPortalPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Support both old "token" and new "heyintake_token" keys for backwards compatibility
  const [token, setToken] = useState(
    localStorage.getItem("heyintake_token") || localStorage.getItem("token")
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback will exchange the session_id and establish the session first.
      if (window.location.hash?.includes('session_id=')) {
        setLoading(false);
        return;
      }
      
      // If user is already set (e.g., from AuthCallback), skip the /me call
      if (user) {
        setLoading(false);
        return;
      }
      
      if (token) {
        setLoading(true); // Set loading while fetching user
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          localStorage.removeItem("heyintake_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token, user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, business } = response.data;
    localStorage.setItem("heyintake_token", access_token);
    localStorage.setItem("token", access_token); // Keep for compatibility
    setToken(access_token);
    setUser(business);
    return business;
  };

  const register = async (email, password, business_name, business_type, referral_source = null, utm_source = null, utm_campaign = null) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      business_name,
      business_type,
      referral_source,
      utm_source,
      utm_campaign
    });
    const { access_token, business } = response.data;
    localStorage.setItem("heyintake_token", access_token);
    localStorage.setItem("token", access_token); // Keep for compatibility
    setToken(access_token);
    setUser(business);
    return business;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("heyintake_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, skipOnboardingCheck = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-medium">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect to onboarding if not completed (unless we're already on onboarding or it's skipped)
  if (!skipOnboardingCheck && !user.onboarding_completed && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// AppRouter - Handles OAuth callback synchronously before other routes
// This prevents race conditions where ProtectedRoute runs before AuthCallback
function AppRouter() {
  const location = useLocation();
  
  // UNIVERSAL REFERRAL CAPTURE - runs on every route change
  // This ensures referral params are captured regardless of entry point
  // (landing page, register, signup, any campaign URL, etc.)
  useEffect(() => {
    captureReferralParams();
  }, [location.pathname, location.search]);
  
  // Check URL fragment (hash) for session_id SYNCHRONOUSLY during render
  // This must happen BEFORE useEffect - useEffect runs AFTER first render, which is too late
  if (window.location.hash?.includes('session_id=')) {
    console.log("[AppRouter] Detected session_id in hash, rendering AuthCallback");
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/partners" element={<PartnerPage />} />
      <Route path="/docs" element={<DocsPage />} />
      <Route path="/docs/:slug" element={<DocsArticlePage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      
      {/* Industry Landing Pages */}
      <Route path="/intake-form-for-lawyers" element={<LawyersPage />} />
      <Route path="/intake-form-for-consultants" element={<ConsultantsPage />} />
      <Route path="/intake-form-for-coaches" element={<CoachesPage />} />
      <Route path="/intake-form-for-agencies" element={<AgenciesPage />} />
      <Route path="/intake-form-for-contractors" element={<ContractorsPage />} />
      <Route path="/intake-form-for-accountants" element={<AccountantsPage />} />
      <Route path="/intake-form-for-therapists" element={<TherapistsPage />} />
      <Route path="/intake-form-for-photographers" element={<PhotographersPage />} />
      <Route path="/intake-form-for-real-estate-agents" element={<RealEstateAgentsPage />} />
      <Route path="/intake-form-for-fitness-trainers" element={<FitnessTrainersPage />} />
      <Route path="/intake-form-for-med-spas" element={<MedSpasPage />} />
      <Route path="/intake-form-for-cleaning-services" element={<CleaningServicesPage />} />
      <Route path="/intake-form-for-event-planners" element={<EventPlannersPage />} />
      <Route path="/intake-form-for-dentists" element={<DentistsPage />} />
      <Route path="/intake-form-for-interior-designers" element={<InteriorDesignersPage />} />
      <Route path="/intake-form-for-financial-advisors" element={<FinancialAdvisorsPage />} />
      
      {/* Comparison Pages */}
      <Route path="/compare/heyintake-vs-intakeq-vs-jotform" element={<HeyIntakeVsIntakeQVsJotform />} />
      
      {/* Blog Pages */}
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/best-ai-intake-software-for-law-firms-2026" element={<LawFirmAIIntakeBlogPost />} />
      <Route path="/blog/top-lead-qualification-tools-for-service-businesses" element={<LeadQualificationToolsBlogPost />} />
      <Route path="/blog/how-to-automate-client-screening-with-ai" element={<AutomateClientScreeningBlogPost />} />
      
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/form/:formSlug" element={<IntakeFormPage />} />
      
      {/* Client Portal - PUBLIC route */}
      <Route path="/client/:submissionId" element={<ClientPortalPage />} />
      
      <Route
        path="/complete-signup"
        element={
          <ProtectedRoute skipOnboardingCheck={true}>
            <CompleteSignupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute skipOnboardingCheck={true}>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <BillingGuard>
            <DashboardPage />
          </BillingGuard>
        }
      />
      <Route
        path="/pipeline"
        element={
          <BillingGuard>
            <PipelinePage />
          </BillingGuard>
        }
      />
      <Route
        path="/submissions"
        element={
          <BillingGuard>
            <SubmissionsPage />
          </BillingGuard>
        }
      />
      <Route
        path="/contacts"
        element={
          <BillingGuard>
            <ContactsPage />
          </BillingGuard>
        }
      />
      <Route
        path="/support"
        element={
          <BillingGuard>
            <SupportPage />
          </BillingGuard>
        }
      />
      <Route
        path="/form-builder"
        element={
          <BillingGuard>
            <FormBuilderPage />
          </BillingGuard>
        }
      />
      <Route
        path="/form-builder/:formId"
        element={
          <BillingGuard>
            <FormBuilderPage />
          </BillingGuard>
        }
      />
      <Route
        path="/email-templates"
        element={
          <BillingGuard>
            <EmailTemplatesPage />
          </BillingGuard>
        }
      />
      <Route
        path="/automations"
        element={
          <BillingGuard>
            <AutomationsPage />
          </BillingGuard>
        }
      />
      <Route
        path="/settings"
        element={
          <BillingGuard>
            <SettingsPage />
          </BillingGuard>
        }
      />
      <Route
        path="/analytics"
        element={
          <BillingGuard>
            <AnalyticsPage />
          </BillingGuard>
        }
      />
      <Route
        path="/customers"
        element={
          <BillingGuard>
            <CustomersPage />
          </BillingGuard>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lead-intelligence"
        element={
          <ProtectedRoute>
            <LeadIntelligencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/lead-intelligence"
        element={
          <ProtectedRoute>
            <AdminLeadIntelligencePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <BillingGuard>
            <TeamPage />
          </BillingGuard>
        }
      />
      <Route path="/join-team" element={<JoinTeamPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <ScrollToTop />
            <EnvironmentBadge />
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </SubscriptionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
